import ItemCover from "components/ItemCover"
import ProductColors from "components/ProductColors"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { Box, Flex, IconButton, Td, ToggleInput, Tr, Typography } from "@strapi/design-system"
import { Eye, Pencil, Trash } from "@strapi/icons"
import { useAlert } from "providers/AlertProvider"
import { Product } from "types"
import { CATEGORIES } from "pages/Products/ProductPage/mock"

export interface IProductTableRow {
  product: Product
  setProductDelete: React.Dispatch<any>
  toggleVisibilityHandler: (product: Product) => void
  onPreview: (productId: string) => Promise<void>
  hideColors?: boolean
}

export const ProductTableRow: React.FC<IProductTableRow> = ({
  product,
  setProductDelete,
  onPreview,
  toggleVisibilityHandler,
}) => {
  const { showAlert } = useAlert()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [visibilityLoading, setVisibilityLoading] = useState<boolean>(false)
  const toggleVisibility = async (product: Product) => {
    setVisibilityLoading(true)
    try {
      await toggleVisibilityHandler(product)
    } catch {
      showAlert({ error: true, text: t("productTableRow.alertVisibilityUpdateFailed") })
    } finally {
      setVisibilityLoading(false)
    }
  }

  return (
    <Tr key={product.id}>
      <Td>
        <Box padding={1}>
          <ItemCover
            cover={product.cover.length ? product.cover : "/none-image.svg"}
            name={product.name}
          />
        </Box>
      </Td>
      <Td>
        <Typography textColor="neutral800">{product.brand}</Typography>
      </Td>
      <Td
        style={{
          maxWidth: "178px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <Typography textColor="neutral800">{product.name}</Typography>
      </Td>
      <Td>
        <Typography textColor="neutral800">{product.type}</Typography>
      </Td>
      {product.categoryId !== CATEGORIES[0].id && (
        <Td style={{ maxWidth: "132px" }}>
          <ProductColors product={product} size={16} />
        </Td>
      )}
      <Td style={{ minWidth: "200px" }}>
        <ToggleInput
          className="cursor-pointer"
          disabled={visibilityLoading}
          checked={product.visible}
          onChange={() => toggleVisibility(product)}
          onLabel={t("productTableRow.toggleVisible")}
          offLabel={t("productTableRow.toggleHidden")}
        />
      </Td>
      <Td style={{ width: "150px" }}>
        <Flex gap={2}>
          <IconButton
            onClick={() => onPreview(product.id)}
            label={t("productTableRow.preview")}
            icon={<Eye />}
          />
          <IconButton
            onClick={() => navigate(`${location.pathname}/edit?id=${product.id}`)}
            label={t("productTableRow.edit")}
            icon={<Pencil />}
          />
          <IconButton
            onClick={() => setProductDelete(product)}
            label={t("productTableRow.delete")}
            icon={<Trash />}
          />
        </Flex>
      </Td>
    </Tr>
  )
}
